/*
 * Design: Mission Control — Strategic Command Dashboard
 * Layout: Three-zone — left nav/timeline, center chat, right preview
 * Color: Slate-charcoal base, teal-cyan accent, gold for revenue
 * Typography: Outfit display, IBM Plex Mono data
 */

import { GameCreatorProvider } from "@/contexts/GameCreatorContext";
import MissionTimeline from "@/components/MissionTimeline";
import ChatPanel from "@/components/ChatPanel";
import GamePreview from "@/components/GamePreview";
import TrendingEvents from "@/components/TrendingEvents";
import { Link, useLocation } from "wouter";
import {
  Zap, ArrowLeft, RotateCcw, PanelRightOpen, PanelRightClose,
  Rocket, TrendingUp, ChevronDown, ChevronUp, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGameCreator, type GameConfig } from "@/contexts/GameCreatorContext";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LaunchGameModal from "@/components/LaunchGameModal";

function CreatorLayout() {
  const { resetCreator, phase, updateGameConfig, sendMessage } = useGameCreator();
  const [showPreview, setShowPreview] = useState(true);
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [showTrending, setShowTrending] = useState(false);
  const [trendingDismissed, setTrendingDismissed] = useState(false);

  // Show the trending suggestion bar during welcome/audience phases (unless dismissed)
  const showTrendingBar = !trendingDismissed && (phase === "welcome" || phase === "audience");

  // Handle "Use This Template" from TrendingEvents
  const handleUseTemplate = useCallback(
    (template: {
      gameTemplate: {
        title: string;
        description: string;
        submissionType: string;
        votingMethod: string;
        entryFee: number;
        maxPlayers: number;
        duration: string;
        winCondition: string;
        viralHook: string;
        socialDistributionMode: string;
        socialHashtag: string;
      };
      eventTitle: string;
      eventDescription: string;
    }) => {
      const { gameTemplate, eventTitle } = template;

      // Map trending template keys to GameConfig keys (fix the mismatch)
      const configUpdates: Partial<GameConfig> = {
        title: gameTemplate.title,
        description: gameTemplate.description,
        gameType: "Creative Challenge",
        entryFee: Math.round(gameTemplate.entryFee / 100), // cents to dollars
        maxPlayers: gameTemplate.maxPlayers,
        duration: gameTemplate.duration,
        winCondition: gameTemplate.winCondition,
        rules: [gameTemplate.description],
        submissionType: gameTemplate.submissionType as GameConfig["submissionType"],
        votingMethod: gameTemplate.votingMethod as GameConfig["votingMethod"],
        socialDistributionMode: gameTemplate.socialDistributionMode as GameConfig["socialDistributionMode"],
        socialHashtag: gameTemplate.socialHashtag,
        marketingHook: gameTemplate.viralHook,
      };

      // Pre-fill the game config
      updateGameConfig(configUpdates);

      // Close the trending panel
      setShowTrending(false);

      // Inject a message into the AI chat so the advisor picks up from the template
      const templateMessage = `I want to create a game based on the trending event "${eventTitle}". The suggested game is: "${gameTemplate.title}" — ${gameTemplate.description}. The viral hook is: "${gameTemplate.viralHook}". Ticket price: $${(gameTemplate.entryFee / 100).toFixed(2)}, max ${gameTemplate.maxPlayers} players, duration: ${gameTemplate.duration}. Please help me refine this game design for my audience.`;

      sendMessage(templateMessage);
    },
    [updateGameConfig, sendMessage]
  );

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      {/* Top Bar */}
      <header className="h-14 border-b border-border flex items-center justify-between px-4 shrink-0 glass-panel">
        <div className="flex items-center gap-3">
          <Link href="/">
            <button className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded bg-neon/20 flex items-center justify-center">
                  <Zap className="w-3.5 h-3.5 text-neon" />
                </div>
                <span className="text-sm font-semibold text-foreground">
                  GP <span className="text-neon">Creator</span>
                </span>
              </div>
            </button>
          </Link>
          <div className="h-5 w-px bg-border" />
          <span className="text-xs text-muted-foreground hidden sm:inline">AI Game Design Mission</span>
        </div>
        <div className="flex items-center gap-2">
          {showTrendingBar && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTrending(!showTrending)}
              className={`text-xs gap-1.5 border-orange-500/30 hover:border-orange-500/50 ${
                showTrending ? "bg-orange-500/10 text-orange-400" : "text-muted-foreground hover:text-orange-400"
              }`}
            >
              <TrendingUp className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Trending</span>
              {showTrending ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={resetCreator}
            className="text-xs text-muted-foreground hover:text-foreground gap-1.5"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">New Mission</span>
          </Button>
          {phase === "launch" && (
            <Button
              size="sm"
              onClick={() => setShowLaunchModal(true)}
              className="bg-neon hover:bg-neon/90 text-background text-xs gap-1.5"
            >
              <Rocket className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Launch on GamePoint</span>
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowPreview(!showPreview)}
            className="text-muted-foreground hover:text-foreground lg:hidden"
          >
            {showPreview ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
          </Button>
        </div>
      </header>

      {/* Mission Timeline */}
      <div className="border-b border-border shrink-0 bg-secondary/20">
        <MissionTimeline />
      </div>

      {/* Trending Events Collapsible Panel */}
      <AnimatePresence>
        {showTrending && showTrendingBar && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="border-b border-orange-500/20 bg-gradient-to-b from-orange-500/5 to-transparent overflow-hidden"
          >
            <div className="max-h-[400px] overflow-y-auto p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-muted-foreground">
                  No game idea? Pick a trending event to get started instantly.
                </p>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-6 h-6 text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setShowTrending(false);
                    setTrendingDismissed(true);
                  }}
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
              <TrendingEvents onUseTemplate={handleUseTemplate} compact />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content — Chat + Preview */}
      <div className="flex-1 flex overflow-hidden">
        {/* Chat Panel — Center */}
        <div className="flex-1 flex flex-col min-w-0">
          <ChatPanel />
        </div>

        {/* Preview Panel — Right */}
        <AnimatePresence>
          {showPreview && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 360, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="border-l border-border bg-secondary/10 overflow-hidden shrink-0 hidden md:block"
            >
              <div className="w-[360px]">
                <GamePreview />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <LaunchGameModal open={showLaunchModal} onOpenChange={setShowLaunchModal} />
    </div>
  );
}

export default function Creator() {
  const { user, isAuthenticated, loading } = useAuth();
  const [, setLocation] = useLocation();

  // Creating a game requires a completed profile (contact info for players)
  useEffect(() => {
    if (!loading && isAuthenticated && user && user.profileComplete === 0) {
      setLocation("/profile-setup");
    }
  }, [loading, isAuthenticated, user, setLocation]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neon/30 border-t-neon rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="max-w-md text-center space-y-5">
          <div className="w-14 h-14 rounded-2xl bg-neon/10 border border-neon/20 flex items-center justify-center mx-auto">
            <Zap className="w-7 h-7 text-neon" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">Sign in to create a game</h1>
            <p className="text-sm text-muted-foreground">
              The AI Game Creator is available to GamePoint members.
            </p>
          </div>
          <Link href={getLoginUrl()}>
            <Button className="bg-neon hover:bg-neon/90 text-background font-semibold">
              Sign In to Continue
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <GameCreatorProvider>
      <CreatorLayout />
    </GameCreatorProvider>
  );
}
